import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { apiClient } from '@/api/client'

export interface ServiceContractSignature {
  mandateReference: string
  signedAt: string
  signerName: string
  accountHolder: string
  creditorId: string | null
  revokedAt: string | null
}

export interface ServiceContractStatus {
  enabled: boolean
  required: boolean
  signedAt: string | null
  pdfAvailable: boolean
  creditorName: string
  creditorId: string | null
  signature: ServiceContractSignature | null
}

export interface SignServiceContractRequest {
  iban: string
  accountHolder: string
  signerName: string
  authorized: boolean
  accepted: boolean
  acceptedText: string
  language: string
}

export const serviceContractQueryKey = (restaurantId: number) => ['service-contract', restaurantId] as const

const contractUrl = (restaurantId: number) => `/api/admin/restaurants/${restaurantId}/service-contract`

const isNotFound = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 404

export function useServiceContractStatus(restaurantId: number | null) {
  return useQuery({
    queryKey: serviceContractQueryKey(restaurantId ?? 0),
    queryFn: async () => (await apiClient.get<ServiceContractStatus>(contractUrl(restaurantId!))).data,
    enabled: restaurantId !== null && restaurantId > 0,
    // 404 = backend without this endpoint yet (bundled app newer than server): answer immediately.
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
  })
}

export type GateState = 'open' | 'loading' | 'error' | 'gate'

/** Only restaurant admins are gated; an older backend (404) must not lock anyone out. */
export function contractGateState(restaurantId: number | null, status: ServiceContractStatus | undefined, error: unknown): GateState {
  if (restaurantId === null) return 'open'
  // A failed background refetch keeps the last answer; it must not unmount a running tablet (order alerts, printing).
  if (status) return status.required ? 'gate' : 'open'
  if (error) return isNotFound(error) ? 'open' : 'error'
  return 'loading'
}

/** The exact statements shown above the signature; stored server-side as evidence of what was accepted. */
export function mandateLines(t: TFunction, status: Pick<ServiceContractStatus, 'creditorName' | 'creditorId'>): string[] {
  const values = { creditorName: status.creditorName, creditorId: status.creditorId }
  return [
    t('mandate.title'),
    t('mandate.creditor', values),
    status.creditorId ? t('mandate.creditorId', values) : t('mandate.creditorIdPending'),
    t('mandate.reference'),
    t('mandate.text', values),
    t('mandate.refund'),
  ]
}

export const normalizeIban = (iban: string) => iban.replace(/\s+/g, '').toUpperCase()

export async function checkIban(iban: string): Promise<boolean> {
  const response = await apiClient.get<{ valid: boolean }>('/iban/check', { params: { iban: normalizeIban(iban) } })
  return response.data.valid
}

export async function fetchContractPdf(restaurantId: number, signed = false): Promise<ArrayBuffer> {
  const response = await apiClient.get<ArrayBuffer>(`${contractUrl(restaurantId)}/${signed ? 'signed-pdf' : 'pdf'}`, {
    responseType: 'arraybuffer',
  })
  return response.data
}

export async function signServiceContract(restaurantId: number, request: SignServiceContractRequest) {
  return (await apiClient.post<{ signedAt: string; mandateReference: string }>(`${contractUrl(restaurantId)}/sign`, request)).data
}

export async function resetServiceContract(restaurantId: number) {
  await apiClient.post(`${contractUrl(restaurantId)}/reset`)
}

export function saveFile(data: ArrayBuffer, fileName: string) {
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
